import React from 'react';
import classnames from 'classnames';
import { useQuery } from '@apollo/react-hooks';
import { useParams, useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import { ErrorBoundary, LoaderColumn, LoaderDashboard, Toast } from 'components';
import { ProfileHero, ProfileSidebar, ProfileSideDrawer, ProfileActivityFeed } from './components';
import { toast } from 'react-toastify';
import { useTranslation } from 'hooks';

import { OrganizationTypes } from 'helpers/constants';

import styles from './ProfileScreen.module.scss';

interface SectionsOptions {
  isCurrentUser: boolean;
  canSeeJobTitle: boolean;
  canEditJobTitle: boolean;
}

export const ProfileScreen = () => {
  return (
    <ErrorBoundary>
      <ProfileSideDrawer />
      <div className={styles.content}>
        <div className={classnames(styles.contentBin, styles.layoutTetris)}>
          <div className={styles.TetrisColumn}>
            <ErrorBoundary>
              <ProfileContent />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

const getCurrentUserQuery = gql`
  query getCurrentUser($userId: String!) {
    me {
      userProfile {
        id
        email
        organization {
          id
          name
          type
        }
      }
    }
    profileByIdentifier(identifier: $userId) {
      ... on Profile {
        organization {
          id
          name
        }
      }
    }
  }
`;

export const ProfileContent = () => {
  const { t } = useTranslation();
  const { userId = '' } = useParams();
  const history = useHistory();

  const { error, data } = useQuery(getCurrentUserQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { userId },
  });

  if (data === undefined && !error) return <ProfileScreenLoader />;

  if (error || !data?.profileByIdentifier) {
    toast(<Toast message={t('toasts.profile-get-user-error')} hasCloseButton />, {
      position: toast.POSITION.BOTTOM_LEFT,
      hideProgressBar: true,
      closeButton: false,
      closeOnClick: true,
      autoClose: 5000,
    });
    history.push(`/`);
  }

  const currentUserOrgName = data?.me?.userProfile?.organization?.name;
  const currentUserOrgType = data?.me?.userProfile?.organization?.type;
  const profileOrgName = data?.profileByIdentifier?.organization?.name;

  const isCurrentUser = userId === data?.me?.userProfile?.id;

  /**
   * Guest Profile View
   * This is the config for the default profile view
   * If no other condition fires, the component will render with these settings
   */
  const sectionConfig: SectionsOptions = {
    isCurrentUser: false,
    canSeeJobTitle: false,
    canEditJobTitle: false,
  };

  // Current User Personal Profile View
  if (isCurrentUser && currentUserOrgType === OrganizationTypes.PERSONAL) {
    sectionConfig.isCurrentUser = true;
  }

  // Current User Work Profile View
  if (isCurrentUser && currentUserOrgType === OrganizationTypes.WORK) {
    sectionConfig.isCurrentUser = true;
    sectionConfig.canSeeJobTitle = true;
    sectionConfig.canEditJobTitle = true;
  }

  // Organization Profile View
  if (currentUserOrgName === profileOrgName && currentUserOrgType === OrganizationTypes.WORK) {
    sectionConfig.isCurrentUser = isCurrentUser;
    sectionConfig.canSeeJobTitle = true;
  }

  return (
    <>
      <ProfileHero
        canSeeJobTitle={sectionConfig.canSeeJobTitle}
        canEditJobTitle={sectionConfig.canEditJobTitle}
        isCurrentUser={sectionConfig.isCurrentUser}
        className={styles.tetrisHero}
      />
      <ProfileSidebar className={styles.tetrisSidebar} />
      <ProfileActivityFeed isCurrentUser={sectionConfig.isCurrentUser} />
    </>
  );
};

export const ProfileScreenLoader = () => {
  return (
    <>
      <div className={styles.tetrisHero}>
        <LoaderColumn type="Row" />
      </div>
      <LoaderColumn />
      <div className={styles.tetrisMain}>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    </>
  );
};
